import { useState, useEffect, useRef } from "react";
import ReactGA from "react-ga4";
import { useWakeLock } from "react-screen-wake-lock";

import "./App.css";

import Header from "./components/UI/header/Header";
import Popup from "./components/UI/Popup";
import Menu from "./components/UI/menu/Menu";

import Paragraph from "./components/UI/Paragraph";
import Link from "./components/UI/Link";
import ButtonPicture from "./components/UI/ButtonPicture";
import ButtonListPicture from "./components/UI/ButtonListPicture";
import ButtonPictureMask from "./components/UI/ButtonPictureMask";
import ButtonDocument from "./components/UI/ButtonDocument";
import ButtonInfo from "./components/UI/ButtonInfo";
import Textarea from "./components/UI/Textarea";
import YesNo from "./components/UI/YesNo";
import Checkbox from "./components/UI/Checkbox";
import InputText from "./components/UI/InputText";
import ButtonSignature from "./components/UI/ButtonSignature";
import Loader from "./components/UI/Loader";

import { getFolder, openFolder, updateFolder } from "./requests/folder";

import { DataType, FolderDataType } from "./types/types";

import { convertData } from "./utils/file";
import { convertOldPageData } from "./utils/converter";
import { saveFormData, loadFormData, deleteFormData } from "./utils/formStorage";

interface ISomeObject {
  [key: string]: DataType;
}

// Per-page popup customization, keyed on folderData.page (the page ID from backend)
// `popupTexts` overrides data.header.texts when set. Supports [variable] substitution.
const POPUP_PAGE_CONFIG: Record<
  string,
  {
    primaryButtonLabel?: string;
    visioUrl?: string;
    visioButtonLabel?: string;
    popupTexts?: string[];
  }
> = {
  "1950": {
    primaryButtonLabel: "Commencer la photo expertise",
    visioUrl:
      "https://outlook.office.com/book/AllianceExpertsVisiosexpertises@alliance-experts.com",
    visioButtonLabel: "Prendre RDV visio",
    popupTexts: [
      "Deux solutions s'offrent à vous :",
      "1. Cliquez sur « Commencer la photo expertise » pour nous transmettre vous-même les photos des dommages.",
      "2. Cliquez sur « Prendre RDV visio » pour réaliser une visio expertise avec un de nos experts.",
      "Sans l'une de ces démarches, nous ne serons pas en mesure de transmettre nos conclusions à votre compagnie et vous risquez de ne pas être indemnisé.",
      "Cordialement, Alliance Experts",
    ],
  },
};

function getPopupCustomization(page: string | number | undefined) {
  if (page == null) return {};
  return POPUP_PAGE_CONFIG[String(page)] || {};
}

function App() {
  const { isSupported, released, request, release } = useWakeLock({
    onRequest: () => {},
    onError: () => {},
    onRelease: () => {},
  });

  useEffect(() => {
    const unloadCallback = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
      return "";
    };

    window.addEventListener("beforeunload", unloadCallback);
    return () => {
      window.removeEventListener("beforeunload", unloadCallback);
    };
  }, []);

  const queryParameters = new URLSearchParams(window.location.search);
  const folderId = queryParameters.get("ID");

  const files: ISomeObject = {
    empty: require("./templates/empty.json"),
    1297: require("./templates/1297.json"), // DEMANDE DE DOCUMENTS MACIF CONSEIL
    1298: require("./templates/1298.json"), // PRE MACIF CONSEIL
    1300: require("./templates/1300.json"), // RELEVE DE DOMMAGES MACIF CONSEIL
    1301: require("./templates/1301.json"), // ROLE EXPERT MACIF CONSEIL
    1305: require("./templates/1305.json"), // EAD SOCIETAIRE MAIF CONSEIL
    1306: require("./templates/1306.json"), // ENVOI DOC CONSEIL
    1308: require("./templates/1308.json"), // PHOTO EXPERTISE CONSEIL
    1765: require("./templates/1765.json"), // ENVOI DOC ALLIANCE
    1766: require("./templates/1766.json"), // EAD SOCIETAIRE MAIF ALLIANCE
    1769: require("./templates/1769.json"), // ROLE EXPERT MACIF ALLIANCE
    1770: require("./templates/1770.json"), // RELEVE DE DOMMAGES MACIF ALLIANCE
    1772: require("./templates/1772.json"), // PRE MACIF ALLIANCE
    1773: require("./templates/1773.json"), // DEMANDE DE DOCUMENTS MACIF ALLIANCE
    1808: require("./templates/1808.json"), // ROLE EXPERT CONSEIL
    1893: require("./templates/1893.json"), // RELEVE DE DOMMAGES MACIF PLURIS CONSEIL
    1938: require("./templates/1765.json"), // ENVOI DOC ALLIANCE
    1937: require("./templates/1937.json"), // EAD SOCIETAIRE MAIF ALLIANCE
    1935: require("./templates/1769.json"), // ROLE EXPERT MACIF ALLIANCE
    1934: require("./templates/1934.json"), // RELEVE DE DOMMAGES MACIF ALLIANCE
    1932: require("./templates/1772.json"), // PRE MACIF ALLIANCE
    1931: require("./templates/1773.json"), // DEMANDE DE DOCUMENTS MACIF ALLIANCE
    1944: require("./templates/1944.json"), // PRE MACIF ALLIANCE
    1945: require("./templates/1945.json"), // ROLE EXPERT ALLIANCE
  };

  const [data, setData] = useState<DataType>(files["empty"]);

  const [legalUrl, setLegalUrl] = useState("");
  const [isLoad, setIsLoad] = useState(true);
  const [isLoadUpload, setIsLoadUpload] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [isPopup, setIsPopup] = useState(true);
  const [folderData, setFolderData] = useState<FolderDataType>();
  const [alreadySent, setAlreadySent] = useState(false);
  const [pageEnteredAt, setPageEnteredAt] = useState(Date.now());
  const [hasInteracted, setHasInteracted] = useState(false);
  const [isFormReady, setIsFormReady] = useState(false);
  const interactionCountRef = useRef(0);
  const abandonedFiredRef = useRef(false);
  const backgroundedAtRef = useRef<number | null>(null);
  const humanEngagedRef = useRef(false);
  const [showResumeToast, setShowResumeToast] = useState(false);
  const totalPage = data.body.length;

  const isElementMissing = (element: any): boolean => {
    if (!element.required) return false;
    if (element.type === "YesNo") return typeof element.checked !== "boolean";
    if (element.value) return false;
    if (element.valueArray?.length) return false;
    if (element.checked) return false;
    return true;
  };

  const missingRequiredFields =
    (data.body[currentPage] &&
      data.body[currentPage].elements?.filter(isElementMissing)) ||
    [];
  const checkEmpty = missingRequiredFields.length;

  const checkNotEmpty =
    data.body[currentPage] &&
    data.body[currentPage].elements?.filter(
      (element) => element.required && !isElementMissing(element)
    ).length;

  const checkTotal =
    data.body[currentPage] &&
    data.body[currentPage].elements?.filter((element) => element.required)
      .length;

  // Auto-save form data to IndexedDB on every change
  useEffect(() => {
    if (folderId && folderData && data.body.length && isFormReady) {
      saveFormData(folderId, data, currentPage);
    }
  }, [data, currentPage, folderId, folderData, isFormReady]);

  useEffect(() => {
    if (!folderId || !folderData || !isFormReady) return;
    if (folderData.status >= 2) return;

    const fireAbandon = (reason: string) => {
      if (abandonedFiredRef.current) return;
      if (!humanEngagedRef.current) return;
      abandonedFiredRef.current = true;
      window.umami?.track("form_abandoned", {
        folder_id: folderId,
        last_page: currentPage,
        total_pages: totalPage,
        missing_required: checkEmpty || 0,
        has_interacted: hasInteracted,
        interaction_count: interactionCountRef.current,
        time_on_page_s: Math.round((Date.now() - pageEnteredAt) / 1000),
        reason,
      });
    };

    const handleBeforeUnload = () => fireAbandon("beforeunload");
    const handlePageHide = () => fireAbandon("pagehide");
    const handleVisibility = () => {
      if (document.visibilityState === "hidden") {
        fireAbandon("visibilitychange_hidden");
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("pagehide", handlePageHide);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("pagehide", handlePageHide);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [
    folderId,
    folderData,
    isFormReady,
    currentPage,
    totalPage,
    checkEmpty,
    hasInteracted,
    pageEnteredAt,
  ]);

  // Track app backgrounding / resuming (user switches tabs/apps without fully leaving)
  useEffect(() => {
    if (!folderId || !folderData || !isFormReady) return;
    if (folderData.status >= 2) return;

    const handleVisibility = () => {
      if (document.visibilityState === "hidden") {
        backgroundedAtRef.current = Date.now();
        window.umami?.track("app_backgrounded", {
          folder_id: folderId,
          page: currentPage,
        });
      } else if (document.visibilityState === "visible") {
        const hiddenDuration = backgroundedAtRef.current
          ? Date.now() - backgroundedAtRef.current
          : 0;
        backgroundedAtRef.current = null;
        if (hiddenDuration > 0) {
          // Reset abandon flag since user came back — they may abandon again later
          abandonedFiredRef.current = false;
          window.umami?.track("app_resumed", {
            folder_id: folderId,
            page: currentPage,
            hidden_duration_s: Math.round(hiddenDuration / 1000),
          });
          // Show "welcome back" toast if hidden for more than 3 seconds
          if (hiddenDuration > 3000) {
            setShowResumeToast(true);
            setTimeout(() => setShowResumeToast(false), 4000);
          }
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibility);
  }, [folderId, folderData, isFormReady, currentPage]);

  // Track first real human interaction (filters out bots, link previewers, security scanners)
  useEffect(() => {
    if (!folderId || !folderData || !isFormReady) return;
    if (folderData.status >= 2) return;

    const mountTime = Date.now();
    let tracked = false;

    const handleEngagement = () => {
      if (tracked) return;
      tracked = true;
      humanEngagedRef.current = true;
      window.umami?.track("human_engaged", {
        folder_id: folderId,
        time_to_engage_ms: Date.now() - mountTime,
      });
      cleanup();
    };

    const events: Array<keyof DocumentEventMap> = [
      "mousemove",
      "scroll",
      "click",
      "touchstart",
      "keydown",
    ];

    const cleanup = () => {
      events.forEach((evt) =>
        document.removeEventListener(evt, handleEngagement)
      );
    };

    events.forEach((evt) =>
      document.addEventListener(evt, handleEngagement, { passive: true })
    );

    return cleanup;
  }, [folderId, folderData, isFormReady]);

  // Toggle scroll hint visibility when there is more content below the viewport
  useEffect(() => {
    if (!isFormReady) return;

    const update = () => {
      const menuEl = document.querySelector(".menu");
      const hintEl = document.querySelector(".scroll-hint");
      const scrollBottom = window.scrollY + window.innerHeight;
      const docHeight = document.documentElement.scrollHeight;
      const hasMoreBelow = docHeight - scrollBottom > 24;
      menuEl?.classList.toggle("has-more-below", hasMoreBelow);
      hintEl?.classList.toggle("visible", hasMoreBelow);
    };

    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    const interval = window.setInterval(update, 600);

    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
      window.clearInterval(interval);
    };
  }, [isFormReady, currentPage, data]);

  useEffect(() => {
    if (folderId) {
      getFolder(folderId)
        .then((result: any) => {
          setIsLoad(false);
          if (result.status >= 2) {
            setAlreadySent(true);
          }

          if (result.success) {
            if (result.status < 2) {
              openFolder(folderId)
                .then(() => {})
                .catch(() => {});
            }

            ReactGA.send({
              hitType: "pageview",
              page: `/${result.page}`,
              title: result.page,
            });

            window.umami?.track("folder_opened", {
              folder_id: folderId,
              page: result.page,
              status: result.status,
              reference: result.reference,
            });

            let templateData: DataType;
            if (files.hasOwnProperty(result.page)) {
              templateData = files[result.page];
            } else if (result.page_data) {
              templateData = convertOldPageData(result.page_data);
            } else {
              templateData = files["empty"];
            }

            // Merge user values from saved data into fresh template structure
            // (ensures new metadata like iconUrl is always up to date)
            const mergeSavedIntoTemplate = (
              template: DataType,
              saved: DataType
            ): DataType => {
              const merged: DataType = JSON.parse(JSON.stringify(template));
              merged.body.forEach((page, pIdx) => {
                const savedPage = saved.body?.[pIdx];
                if (!savedPage) return;
                page.elements?.forEach((el, eIdx) => {
                  const savedEl = savedPage.elements?.[eIdx];
                  if (!savedEl || savedEl.type !== el.type) return;
                  if (savedEl.value !== undefined) el.value = savedEl.value;
                  if (savedEl.valueArray !== undefined)
                    el.valueArray = savedEl.valueArray;
                  if (savedEl.checked !== undefined) el.checked = savedEl.checked;
                  if (savedEl.time !== undefined) el.time = savedEl.time;
                });
              });
              return merged;
            };

            // Restore saved form data from IndexedDB
            loadFormData(folderId).then((saved) => {
              if (saved) {
                let filledFields = 0;
                let imageCount = 0;
                let textCount = 0;
                saved.data.body.forEach((page) => {
                  page.elements?.forEach((el) => {
                    if (el.value || el.valueArray?.length || el.checked) {
                      filledFields++;
                      if (el.type === "ButtonPicture" || el.type === "ButtonCamera" || el.type === "ButtonPictureMask" || el.type === "ButtonDocument") imageCount++;
                      else if (el.type === "ButtonListPicture") imageCount += el.valueArray?.length || 0;
                      else if (el.type === "Textarea" || el.type === "InputText") textCount++;
                    }
                  });
                });
                window.umami?.track("form_restored", {
                  folder_id: folderId,
                  filled_fields: filledFields,
                  images: imageCount,
                  texts: textCount,
                  restored_page: saved.currentPage,
                });
                setData(mergeSavedIntoTemplate(templateData, saved.data));
                setCurrentPage(saved.currentPage);
              } else {
                setData(templateData);
              }
              setIsFormReady(true);
            }).catch(() => {
              setData(templateData);
              setIsFormReady(true);
            });

            setFolderData({
              reference: result.reference,
              email: result.email,
              page: result.page,
              id: result.id,
              status: result.status,
              variables:
                result.data_sent?.home
                  ? Object.keys(result.data_sent.home).map(
                      (key) => result.data_sent.home[key]
                    )
                  : [],
            });

            if (result.reference) {
              if (result.reference.includes("groupelang.fr")) {
                setLegalUrl("https://www.groupelang.fr/donnees-personnelles");
                // @ts-ignore
                document.documentElement.style.setProperty(
                  "--primary-color",
                  "#d73838"
                );
                // @ts-ignore
                document.documentElement.style.setProperty(
                  "--secondary-color",
                  "#9a8585"
                );
                // @ts-ignore
                document.documentElement.style.setProperty(
                  "--text-color",
                  "#451b1b"
                );
              } else if (result.reference.includes("pluris-expertise.com")) {
                setLegalUrl(
                  "https://alliance-experts.com/politique-de-confidentialite"
                );
                // @ts-ignore
                document.documentElement.style.setProperty(
                  "--primary-color",
                  "#71B62B"
                );
                // @ts-ignore
                document.documentElement.style.setProperty(
                  "--secondary-color",
                  "#9A8585"
                );
                // @ts-ignore
                document.documentElement.style.setProperty(
                  "--text-color",
                  "#29430F"
                );
              } else if (
                result.reference.includes("alliance-experts.com") ||
                result.reference.includes("expertconseil.eu")
              ) {
                setLegalUrl(
                  "https://alliance-experts.com/politique-de-confidentialite"
                );
                // @ts-ignore
                document.documentElement.style.setProperty(
                  "--primary-color",
                  "#0054BF"
                );
                // @ts-ignore
                document.documentElement.style.setProperty(
                  "--secondary-color",
                  "#9A8585"
                );
                // @ts-ignore
                document.documentElement.style.setProperty(
                  "--text-color",
                  "#02145B"
                );
              } else {
                // @ts-ignore
                document.documentElement.style.setProperty(
                  "--primary-color",
                  "#F96332"
                );
                // @ts-ignore
                document.documentElement.style.setProperty(
                  "--secondary-color",
                  "#9A8585"
                );
                // @ts-ignore
                document.documentElement.style.setProperty(
                  "--text-color",
                  "#94462d"
                );
              }
            } else {
              // Default Windtransfer colors when reference is empty or unknown
              document.documentElement.style.setProperty(
                "--primary-color",
                "#F96332"
              );
              document.documentElement.style.setProperty(
                "--secondary-color",
                "#9A8585"
              );
              document.documentElement.style.setProperty(
                "--text-color",
                "#94462d"
              );
            }
          }
        })
        .catch((error) => {
          window.umami?.track("folder_load_error", { folder_id: folderId, error: String(error) });
          setIsLoad(false);
        });
    }
  }, []);

  if (!folderId) {
    window.umami?.track("no_folder_id");
    return <Loader show={true} hide={true} text="Référence inconnue" />;
  }

  if (isLoad || (folderData && !isFormReady)) {
    return <Loader show={true} text="Chargement du dossier en cours" />;
  }

  if (!folderData || !data.body.length) {
    if (alreadySent) {
      window.umami?.track("folder_already_sent", { folder_id: folderId });
      return (
        <Loader
          show={true}
          hide={true}
          text="Ce dossier a déjà été envoyé. Merci pour votre participation."
        />
      );
    }

    window.umami?.track("folder_not_found", { folder_id: folderId });
    const oldAppUrl = folderId
      ? `https://app.windtransfer.net/?ID=${folderId}`
      : undefined;
    return (
      <div className="button-signature-modal-container">
        <div className="button-signature-modal">
          <p>Référence inconnue</p>
          {oldAppUrl && (
            <a
              href={oldAppUrl}
              onClick={() => window.umami?.track("click_old_version", { folder_id: folderId })}
              style={{
                display: "inline-block",
                marginTop: "1em",
                padding: "0.6em 1.2em",
                backgroundColor: "#F96332",
                color: "#fff",
                borderRadius: "8px",
                textDecoration: "none",
                fontSize: "0.9em",
              }}
            >
              Ouvrir l'ancienne version
            </a>
          )}
        </div>
      </div>
    );
  }

  if (folderData && folderData.status >= 2) {
    window.umami?.track("folder_already_completed", { folder_id: folderId, reference: folderData.reference });
    return (
      <Loader
        show={true}
        hide={true}
        text={`Merci, les données ont été transmises au cabinet d’Expertise. Dès lors que nous aurons procédé à nos opérations d’Expertise, nous ne manquerons pas de revenir vers vous afin de vous informer du suivi de votre dossier.${folderData?.reference ? ` Nous restons évidemment à votre écoute pour d’éventuels renseignements complémentaires et nous tenons à votre disposition par mail à l’adresse suivante : ${folderData.reference}` : ""}`}
      />
    );
  }

  const trackInteraction = (inputType: string, inputName: string) => {
    interactionCountRef.current += 1;
    if (!hasInteracted) {
      setHasInteracted(true);
      window.umami?.track("form_interaction", {
        folder_id: folderId,
        page: currentPage,
        input_type: inputType,
        input_name: inputName,
        user_email: folderData?.email,
        reference: folderData?.reference,
      });
    }
  };

  const _updateFolder = () => {
    setIsLoadUpload(true);
    const serverData = convertData(data);

    ReactGA.event({
      category: "upload",
      action: "upload",
      value: 0,
    });
    const timeSpent = Math.round((Date.now() - pageEnteredAt) / 1000);
    window.umami?.track("folder_submit_started", { folder_id: folderId, time_on_last_page: timeSpent });

    if (isSupported) {
      request();
    }

    const attemptSubmit = (attempt: number, maxRetries: number) => {
      updateFolder(folderId, serverData)
        .then(() => {
          if (isSupported) {
            release();
          }
          ReactGA.event({
            category: "upload",
            action: "upload_true",
            value: 1,
          });
          window.umami?.track("folder_submit_success", {
            folder_id: folderId,
            attempts: attempt,
          });
          deleteFormData(folderId);
          setIsLoadUpload(false);
          setFolderData({
            ...folderData,
            status: 2,
          });
        })
        .catch((error) => {
          const errorMsg = String(error?.message || error).substring(0, 200);
          const isNetworkError =
            errorMsg.includes("Load failed") ||
            errorMsg.includes("Failed to fetch") ||
            errorMsg.includes("NetworkError") ||
            !navigator.onLine;

          if (isNetworkError && attempt < maxRetries) {
            window.umami?.track("folder_submit_retry", {
              folder_id: folderId,
              attempt,
              error: errorMsg,
            });
            setTimeout(() => attemptSubmit(attempt + 1, maxRetries), attempt * 3000);
            return;
          }

          if (isSupported) {
            release();
          }
          ReactGA.event({
            category: "upload",
            action: "upload_false",
            value: 2,
            label: error,
          });
          window.umami?.track("folder_submit_error", {
            folder_id: folderId,
            error: errorMsg,
            online: navigator.onLine,
            attempts: attempt,
          });
          setIsLoadUpload(false);
          alert("Une erreur est survenue lors de l'envoi. Vérifiez votre connexion et réessayez.");
        });
    };

    attemptSubmit(1, 3);
  };

  return (
    <>
      <Header
        title={data.body[currentPage] && data.body[currentPage].title}
        currentPage={currentPage}
        totalPage={totalPage}
        incrementPercentage={
          checkNotEmpty ? (100 / totalPage) * (checkNotEmpty / checkTotal) : 0
        }
        onInfoClick={() => setIsPopup(true)}
        company={data.header.company}
      />
      <Popup
        show={isPopup}
        onClose={() => {
          window.umami?.track("popup_closed", { folder_id: folderId, page: currentPage });
          setIsPopup(false);
        }}
        status={folderData.status}
        texts={data.header.texts}
        company={data.header.company}
        variables={folderData.variables}
        reference={folderData.reference || ""}
        isTime={data.isTime}
        {...getPopupCustomization(folderData.page)}
      />
      {data.body[currentPage] &&
        data.body[currentPage].elements.map((element, idx) => {
          let rendered: React.ReactNode = null;

          if (element.type === "Paragraph") {
            rendered = (
              <Paragraph
                reference={folderData.reference}
                text={element.text || ""}
                variables={folderData.variables}
              />
            );
          } else if (element.type === "Link") {
            if (element.url) {
              rendered = (
                <Link text={element.text || ""} url={element.url} />
              );
            }
          } else if (element.type === "LinkLegal") {
            if (legalUrl) {
              rendered = (
                <Link text={element.text || ""} url={legalUrl} />
              );
            }
          } else if (
            element.type === "ButtonPicture" ||
            element.type === "ButtonCamera"
          ) {
            rendered = (
              <ButtonPicture
                title={element.title || ""}
                value={element.value}
                onChange={(base64, formatted) => {
                  trackInteraction(element.type, element.title || "");
                  let tmpData = { ...data };
                  tmpData.body[currentPage].elements[idx].value = base64;
                  tmpData.body[currentPage].elements[idx].time = formatted;
                  setData(tmpData);
                }}
                required={element.required}
                isLabelOptionnal={element.isLabelOptionnal}
                iconUrl={element.iconUrl}
                cameraOnly={element.type === "ButtonCamera"}
              />
            );
          } else if (element.type === "ButtonListPicture") {
            rendered = (
              <ButtonListPicture
                title={element.title || ""}
                value={element.valueArray || []}
                onChange={(base64, formatted) => {
                  trackInteraction("ButtonListPicture", element.title || "");
                  let tmpData = { ...data };
                  tmpData.body[currentPage].elements[idx].valueArray = base64;
                  tmpData.body[currentPage].elements[idx].time = formatted;
                  setData(tmpData);
                }}
                required={element.required}
                isLabelOptionnal={element.isLabelOptionnal}
                iconUrl={element.iconUrl}
              />
            );
          } else if (element.type === "ButtonPictureMask") {
            rendered = (
              <ButtonPictureMask
                title={element.title || ""}
                value={element.value}
                onChange={(base64, formatted) => {
                  trackInteraction("ButtonPictureMask", element.title || "");
                  let tmpData = { ...data };
                  tmpData.body[currentPage].elements[idx].value = base64;
                  tmpData.body[currentPage].elements[idx].time = formatted;
                  setData(tmpData);
                }}
                required={element.required}
                mask={element.mask || ""}
                isLabelOptionnal={element.isLabelOptionnal}
                iconUrl={element.iconUrl}
              />
            );
          } else if (element.type === "ButtonDocument") {
            rendered = (
              <ButtonDocument
                title={element.title || ""}
                valid={element.valid || ""}
                value={element.value}
                onChange={(base64, formatted) => {
                  trackInteraction("ButtonDocument", element.title || "");
                  let tmpData = { ...data };
                  tmpData.body[currentPage].elements[idx].value = base64;
                  tmpData.body[currentPage].elements[idx].time = formatted;
                  setData(tmpData);
                }}
                required={element.required}
                isLabelOptionnal={element.isLabelOptionnal}
                iconUrl={element.iconUrl}
              />
            );
          } else if (element.type === "ButtonInfo") {
            rendered = <ButtonInfo title={element.title || ""} />;
          } else if (element.type === "Textarea") {
            rendered = (
              <Textarea
                placeholder={element.placeholder || ""}
                value={element.value || ""}
                onChange={(value) => {
                  trackInteraction("Textarea", element.placeholder || "");
                  let tmpData = { ...data };
                  tmpData.body[currentPage].elements[idx].value = value;
                  setData(tmpData);
                }}
              />
            );
          } else if (element.type === "YesNo") {
            rendered = (
              <YesNo
                text={element.text || ""}
                checked={element.checked}
                onClick={(checked) => {
                  trackInteraction("YesNo", element.text || "");
                  let tmpData = { ...data };
                  tmpData.body[currentPage].elements[idx].checked = checked;
                  setData(tmpData);
                }}
                required={element.required}
                defaultFalse={element.defaultFalse}
              />
            );
          } else if (element.type === "Checkbox") {
            rendered = (
              <Checkbox
                checked={element.checked || false}
                onClick={() => {
                  trackInteraction("Checkbox", element.text || "");
                  let tmpData = { ...data };
                  tmpData.body[currentPage].elements[idx].checked =
                    !element.checked;
                  setData(tmpData);
                }}
                text={element.text || ""}
                required={element.required}
              />
            );
          } else if (element.type === "InputText") {
            rendered = (
              <InputText
                title={element.title || ""}
                placeholder={element.placeholder || ""}
                value={element.value || ""}
                onChange={(value) => {
                  trackInteraction("InputText", element.title || "");
                  let tmpData = { ...data };
                  tmpData.body[currentPage].elements[idx].value = value;
                  setData(tmpData);
                }}
                required={element.required}
              />
            );
          } else if (element.type === "ButtonSignature") {
            rendered = (
              <ButtonSignature
                required={element.required}
                value={element.value || ""}
                onChange={(value) => {
                  trackInteraction("ButtonSignature", "Signature");
                  let tmpData = { ...data };
                  tmpData.body[currentPage].elements[idx].value = value;
                  setData(tmpData);
                }}
              />
            );
          }

          if (!rendered) return null;

          const missing = isElementMissing(element);
          return (
            <div
              key={`${currentPage}-${idx}`}
              className={`form-element${missing ? " is-missing" : ""}`}
              data-required-missing={missing ? "true" : undefined}
            >
              {rendered}
            </div>
          );
        })}

      <div className="scroll-hint" aria-hidden="true">
        <span className="scroll-hint-arrow">▾</span>
        <span>Faites défiler pour voir la suite</span>
      </div>

      <Menu
        currentPage={currentPage}
        totalPage={totalPage}
        disabled={!!checkEmpty}
        onBlockedAttempt={() => {
          window.umami?.track("form_blocked", {
            folder_id: folderId,
            page: currentPage,
            missing_count: checkEmpty || 0,
            missing_fields: missingRequiredFields
              .map((el) => el.title || el.text || el.placeholder || el.type)
              .join(", ")
              .substring(0, 200),
          });

          alert("Veuillez remplir tous les champs obligatoires");

          // Runs after alert dismissal (alert is synchronous and blocks)
          const missingNodes = document.querySelectorAll<HTMLElement>(
            '[data-required-missing="true"]'
          );
          missingNodes.forEach((node) => {
            node.classList.remove("is-blocked-flash");
            // Force reflow to restart the animation
            void node.offsetWidth;
            node.classList.add("is-blocked-flash");
          });
          if (missingNodes.length > 0) {
            missingNodes[0].scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
        }}
        onPageChange={(page: number) => {
          if (page < 0) {
            setIsPopup(true);
          } else {
            const timeSpent = Math.round((Date.now() - pageEnteredAt) / 1000);
            window.umami?.track("page_changed", {
              folder_id: folderId,
              from_page: currentPage,
              to_page: page,
              total_pages: totalPage,
              time_on_page: timeSpent,
            });
            setPageEnteredAt(Date.now());
            setCurrentPage(page);
          }
        }}
        onEnd={() => {
          _updateFolder();
        }}
      />
      <Loader show={isLoadUpload} text="Envoi en cours !" />
      {showResumeToast ? (
        <div className="resume-toast">
          Vous êtes de retour ! Votre formulaire a été sauvegardé.
        </div>
      ) : null}
    </>
  );
}

export default App;
